import { useParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

/**
 * Página pública de política de privacidad por restaurante.
 * Genérica: cumple con los requisitos del art. 13 RGPD (información al interesado).
 * El restaurante puede personalizarla más adelante vía Settings.
 */
export default function PrivacyPolicyPage() {
  const { restaurantId } = useParams();
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Política de privacidad</h1>
            <p className="text-sm text-gray-500">Programa de fidelización — cumple con el RGPD (UE 2016/679)</p>
          </div>
        </div>

        <div className="prose prose-sm max-w-none prose-headings:font-bold space-y-6 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold">1. Responsable del tratamiento</h2>
            <p>
              El restaurante titular del programa de fidelización al que te estás inscribiendo es el responsable del
              tratamiento de tus datos. La plataforma técnica está operada por <strong>MenuGenius</strong> en calidad de
              encargado del tratamiento. Puedes contactar con el restaurante a través de los canales publicados en su
              portal de fidelización.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold">2. Datos que recogemos</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Nombre completo.</li>
              <li>Email y/o teléfono (opcional, para enviar recompensas y notificaciones).</li>
              <li>Fecha de cumpleaños (opcional, para descuentos de cumpleaños).</li>
              <li>Historial de visitas, consumo y puntos acumulados/canjeados.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold">3. Finalidad del tratamiento</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Gestionar el programa de fidelización (acumulación y canje de puntos).</li>
              <li>Enviar comunicaciones relacionadas con tu cuenta (recompensas disponibles, subidas de nivel).</li>
              <li>Enviar, si optas por ello, ofertas de cumpleaños u otras promociones del restaurante.</li>
              <li>Elaborar estadísticas agregadas de uso (anonimizadas).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold">4. Base legal</h2>
            <p>
              Tu consentimiento explícito (art. 6.1.a RGPD), otorgado al marcar la casilla de aceptación durante el
              registro. Puedes retirarlo en cualquier momento sin afectar a la licitud del tratamiento previo.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold">5. Plazo de conservación</h2>
            <p>
              Conservaremos tus datos mientras estés dado de alta en el programa. Si solicitas tu baja (derecho al
              olvido, art. 17 RGPD), se eliminarán en un plazo máximo de 30 días, salvo obligaciones legales de
              retención fiscal o contable.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold">6. Destinatarios</h2>
            <p>
              No cedemos tus datos a terceros ajenos, salvo proveedores tecnológicos necesarios para operar el servicio
              (hosting, envío de emails, pasarelas de pago), debidamente contratados como encargados del tratamiento.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold">7. Tus derechos</h2>
            <p>
              Puedes ejercer en cualquier momento tus derechos de acceso, rectificación, supresión, oposición, limitación
              del tratamiento y portabilidad. Solo tienes que contactar con el restaurante. También tienes derecho a
              presentar una reclamación ante la Agencia Española de Protección de Datos (<a href="https://www.aepd.es" target="_blank" rel="noreferrer" className="text-emerald-700 underline">aepd.es</a>).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold">8. Seguridad</h2>
            <p>
              Implementamos medidas técnicas y organizativas apropiadas: cifrado en tránsito (TLS), almacenamiento
              cifrado, control de acceso y registro de actividad.
            </p>
          </section>

          <p className="text-xs text-gray-400 pt-6 border-t">
            Versión 1.0 · Esta política se aplica al restaurante {restaurantId}. Para más información específica,
            contacta directamente con el restaurante.
          </p>
        </div>
      </div>
    </div>
  );
}
